import { useEffect, useRef, useState } from "react";
import ConfirmationModal from "../../../components/layout/modal/ConfirmationModal";
import { useUtilDataQuery } from "../../profile/profileApi";
import { useAssignProductMutation } from "../productsApi";
import { handleRTKQuery } from "../../../helpers/utils/RtkQueryUtils";
import SkeletonCell from "../../../components/common/SkeletonCell";

interface AssignToSectionProps {
    isUserManager?: any;
    setShowAssignToModal: (value: boolean) => void;
    assignedTo: string | null;
    setAssignedTo: (value: string | null) => void;
    assignedTeam: string | null;
    setAssignedTeam: (value: string | null) => void;
    productId: string;
    setProductId: (value: string) => void;
    setShowTeamListModal: (value: boolean) => void;
    setShowTeamManagerModal: (value: boolean) => void;
    isHoa?: boolean;
}

export function AssignToSection({
    isUserManager,
    setShowAssignToModal,
    assignedTo,
    setAssignedTo,
    assignedTeam,
    setAssignedTeam,
    productId,
    setProductId,
    setShowTeamListModal,
    setShowTeamManagerModal,
    isHoa = false
}: AssignToSectionProps) {
    const [disabled, setDisabled] = useState<boolean>(false);
    const [showUnAssignConfirmModal, setShowUnAssignConfirmModal] = useState<boolean>(false);
    const { data: utilInfo } = useUtilDataQuery("");
    const [showDropdown, setShowDropdown] = useState<boolean>(false)
    const [updateAssignmentInfoApi, updateAssignmentInfoApiResponse] = useAssignProductMutation();
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
                function handleClickOutside(event: MouseEvent) {
                    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                        setShowDropdown(false);
                    }
                }
    
                if (showDropdown) {
                    document.addEventListener("mousedown", handleClickOutside);
                }
    
                return () => {
                    document.removeEventListener("mousedown", handleClickOutside);
                };
            }, [showDropdown]);

    const handleAssignProduct = async () => {
        setDisabled(true);
        await handleRTKQuery(() => {
            updateAssignmentInfoApi({ productId: productId, userId: utilInfo.userId, isAssigned: "false" });
        }, () => {
            setShowDropdown(false)
            setDisabled(false)
        });
    }

    const handleUnAssignProduct = async () => {
        setDisabled(true);
        await handleRTKQuery(() => {
            updateAssignmentInfoApi({ productId: productId, userId: null, isAssigned: "true" });
        }, () => {
            setShowUnAssignConfirmModal(false);
            setShowDropdown(false)
            setDisabled(false)
        });
    }

    return (
        <>
            {isUserManager !== undefined ? (
                <div ref={dropdownRef}
                    className={
                        isUserManager || assignedTo === utilInfo?.userName
                            ? !isHoa
                                ? "cursor update_status_dropbox cm_btn relative"
                                : "cursor update_status_dropbox cm_btn hoa_bg relative"
                            : "cursor update_status_dropbox cm_btn disabled_button relative"
                    }
                >
                    {utilInfo && !isUserManager?.data && (
                        <div
                            className={
                                assignedTo === utilInfo?.userName
                                    ? `flex items-center text-text-0 border border-transparent rounded-[11px] m-[7px_4px] px-[14px] py-[5px] ml-0 cursor ${isHoa ? 'bg-highlight-1' : 'bg-[rgb(70,130,180)]'
                                    }`
                                    : "bg-text-3 pl-2 p-1 pr-2 rounded-[11px] text-text-0 text_color mr-1"
                            }
                        >
                            <button
                                aria-hidden="true"
                                onClick={() => {
                                    if (assignedTo === utilInfo?.userName) {
                                        setProductId(productId);
                                        setAssignedTo(assignedTo);
                                        setShowTeamManagerModal(true);
                                    }
                                }}
                            >
                                Escalate
                            </button>
                            {assignedTo === utilInfo?.userName && (
                                <span
                                    className="inline-block border-l pl-2 items-baseline cursor-pointer border-surface-0 justify-center"
                                    onClick={() => {
                                        setShowDropdown(!showDropdown);
                                    }}
                                >
                                    <i className="fa fa-sort-desc " aria-hidden="true"></i>
                                </span>
                            )}
                        </div>
                    )}

                    {isUserManager?.data && (
                        <div
                            className={
                                isHoa
                                    ? "flex items-center gap-1 text-text-0 bg-highlight-1 border border-transparent rounded-[11px] m-[7px_4px] px-[14px] py-[5px] ml-0 cursor"
                                    : "flex items-center gap-1 text-text-0 bg-[rgb(70,130,180)] border border-transparent rounded-[11px] m-[7px_4px] px-[14px] py-[5px] ml-0 cursor"
                            }
                        >
                            <button
                                className="text_color"
                                onClick={() => {
                                    setProductId(productId);
                                    setAssignedTo(assignedTo);
                                    setShowAssignToModal(true);
                                }}
                                aria-hidden="true"
                            >
                                Assign
                            </button>
                            <span
                                className="inline-block border-l pl-1 items-baseline cursor-pointer border-surface-0 justify-center"
                                onClick={() => {
                                    setShowDropdown(!showDropdown);
                                }}>
                                <i className="fa fa-sort-desc relative bottom-1 ml-1" aria-hidden="true"></i>
                            </span>
                        </div>
                    )}

                    {showDropdown && (
                        <div
                            className="cursor dropdown dropdown-button-opener absolute right-[10px] bg-text-0 text-left z-[9] top-[39px] w-48 border rounded border-text-1">
                            <div className="dropdown-menu">
                                {isUserManager?.data && (
                                    <a
                                        className="dropdown-item p-2 block border-b border-text-1 hover:bg-surface-6 hover:text-text-0 cursor-pointer"
                                        onClick={() => {
                                            setProductId(productId);
                                            setAssignedTeam(assignedTeam);
                                            setShowTeamListModal(true);
                                        }}
                                    >
                                        Assign to Team
                                    </a>
                                )}

                                {utilInfo &&
                                    isUserManager?.data &&
                                    (!assignedTo || assignedTo !== utilInfo?.userName) && (
                                        <a
                                            className="dropdown-item block p-2 border-b border-text-1 hover:bg-surface-6 cursor-pointer hover:text-text-0"
                                            onClick={handleAssignProduct}
                                        >
                                            Assign to Me
                                        </a>
                                    )}

                                {utilInfo &&
                                    assignedTo &&
                                    assignedTo === utilInfo?.userName && (
                                        <a
                                            className="dropdown-item block p-2 border-b border-text-1 hover:bg-surface-6 cursor-pointer hover:text-text-0"
                                            onClick={() => setShowUnAssignConfirmModal(true)}
                                        >
                                            Unassign
                                        </a>
                                    )}
                            </div>
                            {showUnAssignConfirmModal && (
                                <ConfirmationModal
                                    title="Unassign Product"
                                    bodyMessage="Are you sure you want to unassign this product?"
                                    onSubmit={handleUnAssignProduct}
                                    show={showUnAssignConfirmModal}
                                    setShow={setShowUnAssignConfirmModal}
                                    isLoading={disabled}
                                    isHoa={isHoa}
                                />
                            )}
                        </div>
                    )}

                </div>
            ) : (
                <SkeletonCell />
            )}
        </>
    );
}
